// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page, { PageProps as ContainerProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngUtilities from "@tps/App/Components/Common/XngUtilities";
import loadSystemList from "@tps/App/Handlers/SystemList/LoadSystemList";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { getLogoImageWidget } from "@tps/App/Store/Selectors/HeaderSelectors";
import { getSystemListSelector } from "@tps/App/Store/Selectors/SystemListSelectors";
import { color, companyLogoItem, mergeWithCss, xScreen } from "@tps/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const pageType = getCurrentPage(state).type;
    const location = getLocation(state);

    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        location,
        isCheckoutPages:
            pageType === "CheckoutShippingPage" ||
            pageType === "CheckoutReviewAndSubmitPage" ||
            pageType === "OrderConfirmationPage" ||
            pageType === "RfqRequestQuotePage" ||
            pageType === "RfqConfirmationPage",
        notificationBarFromState: state.xHeader.notificationBar,
        showDropShadow: state.xHeader.showDropShadow,
        // XNG-Changes: adding logo to print view
        logoImageWidget: getLogoImageWidget(state),
        searchAttributesSystemList: getSystemListSelector(state, "HomePageSearchAttribute"),
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
    loadSystemList,
};

export const HeaderSize: any = {
    default: 133,
    // sticky: 94,
    mobile: 89,
    top: 0,
    checkoutHeader: 56,
    checkoutMobileHeader: 56,
    notificationBar: 33,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    header?: ContainerProps;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    headerContentWrapper?: InjectableCss;
    logoLinksSearchWrapper?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    notificationWrapper?: InjectableCss;
    secondaryNavigationPage?: ContainerProps;
    logoLinksSearchPage?: ContainerProps;
    notificationPage?: ContainerProps;
    // XNG-Changes
    printLogo?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    header: {
        as: "header",
        fullWidth: [true, true, true, true, true],
        padding: 0,
        css: css`
            position: sticky !important;
            top: 0;
            left: 0;
            right: 0;
            z-index: 1286;
            background: ${getColor("common.white")} !important;
            height: ${HeaderSize.default}px;
            box-shadow: 0 1px 0 0 ${color("common.border")};

            ${({ notificationBar }: any) => {
                if (!notificationBar) {
                    return `
                    height: ${HeaderSize.default - HeaderSize.notificationBar}px;
                `;
                }
            }}

            ${xScreen("lte", "md")} {
                position: fixed !important;
                height: ${HeaderSize.mobile}px;
                ${({ notificationBar }: any) => {
                    if (!notificationBar) {
                        return `
                        height: ${HeaderSize.mobile - HeaderSize.notificationBar}px;
                    `;
                    }
                }}
            }
            // Preventing header from appearing on each page in print view
            @media print {
                position: relative !important;
            }
            ${({ isCheckoutPages }: any) => {
                if (isCheckoutPages) {
                    return `
                    height: ${HeaderSize.checkoutHeader}px;
                    overflow: hidden;

                    @media (max-width: 1024px) {
                        height: ${HeaderSize.checkoutMobileHeader}px;
                    }
                    `;
                }
                return `

                `;
            }}

            @media print {
                display: none !important;
            }

            &:after {
                transition: 1000ms all;
                content: " ";
                position: fixed;
                top: 66px;
                bottom: 0;
                left: 0;
                right: 0;
                z-index: 10;
                visibility: hidden;
                background: transparent;
                ${({ notificationBar }: any) =>
                    notificationBar
                        ? css`
                              top: ${HeaderSize.default}px;
                          `
                        : css`
                              top: ${HeaderSize.default - HeaderSize.notificationBar}px;
                          `}

                ${xScreen("lte", "md")} {
                    ${({ notificationBar }: any) =>
                        notificationBar
                            ? css`
                                  top: ${HeaderSize.mobile}px;
                              `
                            : css`
                                  top: ${HeaderSize.mobile - HeaderSize.notificationBar}px;
                              `}
                }

                ${({ showDropShadow }: any) =>
                    showDropShadow &&
                    css`
                        background: ${color("common.black", 0.33)};
                        visibility: visible;
                    `}
            }
        `,
    },
    headerContentWrapper: {
        css: css`
            @media print {
                position: static;
            }
        `,
    },
    logoLinksSearchWrapper: {
        css: css`
            background: ${getColor("common.white")};

            ${({ isInShell }: any) => isInShell && `height: auto;`}
        `,
    },
    notificationWrapper: {
        css: css`
            height: 33px;
            display: flex;
            align-items: center;

            ${({ notificationBar }: any) => {
                if (notificationBar) {
                    return css`
                        background: ${color("primary.main")};
                    `;
                }
                if (!notificationBar) {
                    return css`
                        display: none;
                    `;
                }
            }}

            ${({ isInShell }: any) => isInShell && `height: auto;`}
        `,
    },
    secondaryNavigationWrapper: {
        css: css`
            background: ${getColor("common.ui2")};
            height: 34px;
            display: flex;
            align-items: center;
            border-bottom: 1px solid ${color("common.border")};
            ${xScreen("lte", "md")} {
                display: none;
            }

            ${({ isInShell }: any) => isInShell && `height: auto;`}
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    logoLinksSearchPage: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    secondaryNavigationPage: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    notificationPage: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    printLogo: {
        ...mergeWithCss(companyLogoItem, {
            css: css`
                img {
                    max-width: 110px;
                    max-height: 110px;
                }
            `,
        }),
    },
};

const styles = headerStyles;
const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    location,
    isCheckoutPages,
    notificationBarFromState,
    showDropShadow,
    // XNG-Changes:
    logoImageWidget,
    searchAttributesSystemList,
    loadSystemList,
}: Props) => {
    const [notificationBar, setNotificationBar] = useState(false);

    // XNG-Change
    React.useEffect(() => {
        if (!searchAttributesSystemList.isLoading && !searchAttributesSystemList?.values) {
            loadSystemList({ name: "HomePageSearchAttribute" });
        }
    }, []);

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const closedState = sessionStorage?.getItem("closed");
        setNotificationBar(!(closedState && closedState === "true"));
    }, [notificationBarFromState]);

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    const { isInShell } = useShellContext();

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler} aria-label="View My Dashboard">
                            {translate("View My Dashboard")}
                        </Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            {/* XNG-Changes: add logo for print view */}
            {logoImageWidget && (
                <StyledWrapper {...styles.printLogo}>
                    <img src={logoImageWidget.logoImage} alt={translate("Home")} />
                </StyledWrapper>
            )}
            <Page {...styles.header} id="header" {...({ isCheckoutPages, notificationBar, showDropShadow } as any)}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <StyledWrapperX {...styles.headerContentWrapper} className="header_content-wrapper">
                    {!isCheckoutPages && (
                        <StyledWrapper
                            {...styles.notificationWrapper}
                            {...({ notificationBar, isInShell } as any)}
                            className="notification-wrapper"
                        >
                            <Page as="div" {...styles.notificationPage}>
                                <Zone contentId={id} zoneName="notification" />
                            </Page>
                        </StyledWrapper>
                    )}
                    {!isCheckoutPages && (
                        <StyledWrapper
                            {...styles.secondaryNavigationWrapper}
                            {...({ isInShell } as any)}
                            className="secondary-navigation-wrapper"
                        >
                            <Page as="div" {...styles.secondaryNavigationPage}>
                                <Zone contentId={id} zoneName="secondaryNavigation" />
                            </Page>
                        </StyledWrapper>
                    )}
                    <StyledWrapper
                        {...styles.logoLinksSearchWrapper}
                        {...({ isInShell } as any)}
                        className="logo-links-search-wrapper"
                    >
                        <Page as="div" {...styles.logoLinksSearchPage}>
                            <Zone contentId={id} zoneName="LogoLinksSearch" />
                        </Page>
                    </StyledWrapper>
                </StyledWrapperX>

                <span ref={afterHeader} tabIndex={-1} />
                <XngUtilities />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
